import { useCallback, useEffect, useState } from 'react';

import { getLocalStorageItem, setLocalStorageItem } from '@atlassian/search-common';

import { getSearchPageFilterConfigKey } from '../../constants/filters';
import type { ProductKeys } from '../../constants/products';
import {
	SMARTLINKS_RESOURCE_TYPE_PREFIX,
	type ThirdPartyConfigsBootstrap,
} from '../../constants/schemas/3p-config';
import { serveRightRailImprovementsWithoutExposure } from '../../utils/experiments';

export const useAppFilterState = (): any[] => {
	const [storedState, setStoredState] = useState(
		getLocalStorageItem(getSearchPageFilterConfigKey()) || {},
	);

	const setFilterHidden = useCallback(
		(productKey: ProductKeys, value: boolean) => {
			const newValue = {
				...storedState,
				filters: {
					...storedState?.filters,
					[productKey]: {
						hidden: value,
					},
				},
			};
			setStoredState(newValue);
			setLocalStorageItem(getSearchPageFilterConfigKey(), JSON.stringify(newValue));
		},
		[storedState, setStoredState],
	);

	return [storedState, { setFilterHidden }];
};

// Hide smartlink filters by default
export const useHideSmartLinks = ({
	thirdPartyConfigs,
	storedState,
	setFilterHidden,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	storedState: any;
	setFilterHidden: (productKey: ProductKeys, value: boolean) => void;
}): void => {
	useEffect(() => {
		// Do NOT hide smart link filters by default for the Test cohort.
		if (serveRightRailImprovementsWithoutExposure()) {
			return;
		}

		Object.entries(thirdPartyConfigs).forEach(([productKey, config]) => {
			const hasFilter = storedState?.filters && storedState.filters[productKey] !== undefined;
			if (config?.resourceType?.includes(SMARTLINKS_RESOURCE_TYPE_PREFIX) && !hasFilter) {
				setFilterHidden(productKey as ProductKeys, true);
			}
		});
	}, [thirdPartyConfigs, storedState?.filters, setFilterHidden]);
};
